import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SidebarContainer, SidebarItem, SidebarIcon, SidebarText, CollapseButton, SidebarOverlay } from './sidebar.styles';
import { FiMenu, FiChevronLeft } from 'react-icons/fi';
import { secoesVisiveis } from '../../navegacao/secoes';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface SidebarProps {
  activeItem?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeItem = 'dashboard', isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAdminAccess, isManager, user } = useAuth();
  const { t } = useLanguage();

  // Debug: log das verificações de permissão (executado apenas quando há mudanças)
  React.useEffect(() => {
    console.log("Sidebar - Usuário:", user);
    console.log("Sidebar - hasAdminAccess():", hasAdminAccess());
    console.log("Sidebar - isManager():", isManager());
  }, [user, hasAdminAccess, isManager]);

  // Mapeamento de rotas
  const routes = {
    dashboard: '/',
    users: '/users',
    organizations: '/organizations',
    compliance: '/compliance',
    rules: '/rules',
    folders: '/folders',
    results: '/results'
  };

  // Função para determinar qual item está ativo baseado na rota atual
  const getActiveItem = (path: string) => {
    const currentPath = location.pathname;

    // Caso especial para o dashboard
    if (path === 'dashboard') {
      return currentPath === '/';
    }

    // Caso especial para results - deve reconhecer tanto /results quanto /compliance/results
    if (path === 'results') {
      return currentPath === '/results' || currentPath === '/compliance/results';
    }

    // Caso especial para compliance - não deve marcar como ativo quando estiver em /compliance/results
    if (path === 'compliance') {
      return currentPath.startsWith('/compliance') && currentPath !== '/compliance/results';
    }

    // Para outras rotas, verifica se o pathname começa com a rota do item
    return currentPath.startsWith(routes[path as keyof typeof routes]);
  };

  // Função para lidar com clique em item do menu
  const handleMenuItemClick = (callback: () => void) => {
    // Executa a navegação primeiro
    callback();

    // No mobile, sempre fecha o sidebar após clique em item
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    console.log('Menu item clicked - isMobile:', isMobile, 'isCollapsed:', isCollapsed);

    if (isMobile && onToggleCollapse) {
      // No mobile, se o sidebar está visível (!isCollapsed), fecha ele (isCollapsed = true)
      if (!isCollapsed) {
        console.log('Closing sidebar in mobile...');
        // Executa imediatamente sem delay para resposta mais rápida
        onToggleCollapse();
      }
    }
  };

  return (
    <>
      <SidebarOverlay $visible={!isCollapsed} onClick={onToggleCollapse} />

      {/*
        O botão fica FORA do SidebarContainer, e isso é o conserto de um defeito real.

        Ele é desenhado para parecer centrado na borda direita da barra: 32px de largura deslocados
        16px para fora. Só que o container tem `overflow-x: hidden` (necessário, senão o rótulo longo
        gera rolagem horizontal quando a barra recolhe para 80px), e overflow diferente de `visible`
        CORTA o que passa da borda. Resultado: exatamente metade do botão desaparecia.

        Como irmão, ele se posiciona pela viewport — o container é `position: fixed; left: 0`, então
        `left` calculado a partir da largura atual põe o botão no mesmo lugar pretendido, agora sem
        nada que o recorte. O `overflow` do container fica intacto.
      */}
      <CollapseButton onClick={onToggleCollapse} $collapsed={isCollapsed}>
        {isCollapsed ? FiMenu({ size: 20 }) : FiChevronLeft({ size: 20 })}
      </CollapseButton>

      <SidebarContainer $collapsed={isCollapsed}>
        {/*
          Uma unica fonte para as secoes: `navegacao/secoes.tsx`, a mesma que a tela inicial usa.

          Antes as sete secoes eram JSX escrito a mao aqui, cada uma repetindo icone, rota e regra de
          permissao. Com os atalhos da tela inicial consumindo a mesma lista, uma secao nova aparece
          nos dois lugares sozinha — e, mais importante, a regra de quem pode ver cada uma existe em
          UM lugar. Duas copias de regra de permissao divergem em silencio: o atalho continua
          visivel para quem perdeu acesso, sem erro e sem teste vermelho.

          O DOM renderizado e o mesmo de antes: mesma ordem, mesmos icones, mesmos rotulos.
        */}
        {secoesVisiveis({ admin: hasAdminAccess(), manager: isManager() }).map(secao => (
          <SidebarItem
            key={secao.chave}
            $active={getActiveItem(secao.chave)}
            onClick={() => handleMenuItemClick(() => secao.navegar(navigate))}
            $collapsed={isCollapsed}
            data-tooltip={isCollapsed ? t(secao.rotulo) : ""}
          >
            <SidebarIcon $collapsed={isCollapsed}>{secao.icone(20)}</SidebarIcon>
            <SidebarText $collapsed={isCollapsed}>{t(secao.rotulo)}</SidebarText>
          </SidebarItem>
        ))}
      </SidebarContainer>
    </>
  );
};

export default Sidebar;